import React from 'react';
import { Heart, Scissors, Scale, Shield, CheckCircle, Star, Clock, Award, Users, ArrowRight, Calendar, Phone, BookOpen } from 'lucide-react';

const Services = () => {
  const whitecolor = { color: '#f7f7f7' };

  return (
    <>
      <style>{`
h1.heading-primary.text-white { color: #fff;}
p.text-large.text-white.mb-8 { color: #fff;}
.stat-number.text-white { color: #fff;}
.stat-label.text-white { color: #fff;}
h2.heading-secondary.text-white.mb-2 { color: #fff;}
      `}</style>

      <div data-testid="services-page">
        {/* Hero Section with Medical Background */}
        <section className="hero-services" data-testid="services-hero">
          <div className="container">
            <div className="grid grid-2 gap-16 items-center min-h-[80vh]">
              <div>
                <div className="specialty-badge mb-6">
                  <Scissors className="inline mr-2" size={16} />
                  Advanced Surgical Excellence
                </div>
                <h1 className="heading-primary text-white" data-testid="services-title">
                  Comprehensive Surgical Services
                </h1>
                <p className="text-large text-white mb-8" data-testid="services-subtitle">
                  Expert surgical care using state-of-the-art minimally invasive ‘keyhole’ techniques for gallbladder, hernia and gastrointestinal conditions, with holistic non-surgical weight assessment and management.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a href="/contact" className="btn btn-primary btn-large">
                    <Calendar className="mr-2" size={20} />
                    Schedule Consultation
                  </a>
                  <a href="/patient-info" className="btn btn-secondary btn-large">
                    Patient Information
                  </a>
                </div>
              </div>

              <div className="grid grid-2 gap-6">
                <div className="stat-card-hero">
                  <div className="stat-number text-white">98%</div>
                  <div className="stat-label text-white">Satisfaction</div>
                </div>
                <div className="stat-card-hero">
                  <div className="stat-number text-white">2000+</div>
                  <div className="stat-label text-white">Procedures</div>
                </div>
                <div className="stat-card-hero">
                  <div className="stat-number text-white">24hr</div>
                  <div className="stat-label text-white">Typical Recovery*</div>
                </div>
                <div className="stat-card-hero">
                  <div className="stat-number text-white">15+</div>
                  <div className="stat-label text-white">Years’ Experience</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Navigation */}
        <section className="section bg-gradient-modern" data-testid="services-nav">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="heading-secondary">Our Specialised Services</h2>
              <p className="text-large">
                Click on any service below to learn more about our care
              </p>
            </div>

            <div className="grid grid-3 gap-8">
              <a href="#keyhole" className="service-nav-card group">
                <div className="service-nav-icon">
                  <Scissors className="text-blue-600 group-hover:text-white transition-colors" size={32} />
                </div>
                <h3 className="font-bold text-lg mb-2">Keyhole Surgery</h3>
                <p className="text-gray-600 group-hover:text-gray-300 transition-colors">Laparoscopic & minimally invasive</p>
                <ArrowRight className="mt-4 text-blue-600 group-hover:text-white transition-colors" size={20} />
              </a>

              <a href="#abdominal-gi" className="service-nav-card group">
                <div className="service-nav-icon">
                  <Users className="text-blue-600 group-hover:text-white transition-colors" size={32} />
                </div>
                <h3 className="font-bold text-lg mb-2">Abdominal & GI Symptoms</h3>
                <p className="text-gray-600 group-hover:text-gray-300 transition-colors">Assessment & management</p>
                <ArrowRight className="mt-4 text-blue-600 group-hover:text-white transition-colors" size={20} />
              </a>

              <a href="#gord" className="service-nav-card group">
                <div className="service-nav-icon">
                  <BookOpen className="text-blue-600 group-hover:text-white transition-colors" size={32} />
                </div>
                <h3 className="font-bold text-lg mb-2">GORD</h3>
                <p className="text-gray-600 group-hover:text-gray-300 transition-colors">Gastro-oesophageal reflux disease</p>
                <ArrowRight className="mt-4 text-blue-600 group-hover:text-white transition-colors" size={20} />
              </a>

              <a href="#gallbladder" className="service-nav-card group">
                <div className="service-nav-icon">
                  <Heart className="text-blue-600 group-hover:text-white transition-colors" size={32} />
                </div>
                <h3 className="font-bold text-lg mb-2">Gallstones & Gallbladder</h3>
                <p className="text-gray-600 group-hover:text-gray-300 transition-colors">Laparoscopic cholecystectomy</p>
                <ArrowRight className="mt-4 text-blue-600 group-hover:text-white transition-colors" size={20} />
              </a>

              <a href="#hernia" className="service-nav-card group">
                <div className="service-nav-icon">
                  <Shield className="text-blue-600 group-hover:text-white transition-colors" size={32} />
                </div>
                <h3 className="font-bold text-lg mb-2">Hernia</h3>
                <p className="text-gray-600 group-hover:text-gray-300 transition-colors">Assessment & management</p>
                <ArrowRight className="mt-4 text-blue-600 group-hover:text-white transition-colors" size={20} />
              </a>

              <a href="#lipomas" className="service-nav-card group">
                <div className="service-nav-icon">
                  <Award className="text-blue-600 group-hover:text-white transition-colors" size={32} />
                </div>
                <h3 className="font-bold text-lg mb-2">Lipomas, Lumps & Bumps</h3>
                <p className="text-gray-600 group-hover:text-gray-300 transition-colors">Assessment & management</p>
                <ArrowRight className="mt-4 text-blue-600 group-hover:text-white transition-colors" size={20} />
              </a>

              <a href="#weight-non-surgical" className="service-nav-card group">
                <div className="service-nav-icon">
                  <Scale className="text-blue-600 group-hover:text-white transition-colors" size={32} />
                </div>
                <h3 className="font-bold text-lg mb-2">Non-Surgical Weight</h3>
                <p className="text-gray-600 group-hover:text-gray-300 transition-colors">Holistic review & packages</p>
                <ArrowRight className="mt-4 text-blue-600 group-hover:text-white transition-colors" size={20} />
              </a>


              {/* Endoscopy (nav) */}
<a href="#endoscopy" className="service-nav-card group">
  <div className="service-nav-icon">
    <BookOpen className="text-blue-600 group-hover:text-white transition-colors" size={32} />
  </div>
  <h3 className="font-bold text-lg mb-2">Endoscopy</h3>
  <p className="text-gray-600 group-hover:text-gray-300 transition-colors">Diagnostic & therapeutic</p>
  <ArrowRight className="mt-4 text-blue-600 group-hover:text-white transition-colors" size={20} />
</a>

            </div>
          </div>
        </section>

        {/* Main Services */}
        <section className="section" data-testid="main-services">
          <div className="container">
            <div className="space-y-24">

              {/* Keyhole Surgery */}
              <div id="keyhole" className="service-detail-card" data-testid="keyhole-service">
                <div className="service-detail-header">
                  <div className="service-detail-image"
                    style={{ backgroundImage: `url('https://images.unsplash.com/photo-1606685622133-ed6fbaaf445c?crop=entropy&cs=srgb&fm=jpg&q=85')` }}>
                  </div>
                  <div className="service-detail-content">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="service-icon-large">
                        <Scissors className="text-white" size={40} />
                      </div>
                      <div>
                        <h2 className="heading-secondary text-white mb-2">Laparoscopic & Minimally Invasive ‘Keyhole’ Surgery</h2>
                        <p className="text-white opacity-90">Smaller incisions, reduced pain, quicker recovery</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="service-detail-body">
                  <div className="grid grid-2 gap-12">
                    <div>
                      <h3 className="heading-tertiary mb-6">What this involves</h3>
                      <p className="text-lg leading-relaxed mb-8">
                        Modern laparoscopic approaches for suitable abdominal conditions, aiming for minimal scarring and faster return to normal activity.
                      </p>
                      <ul className="procedure-list">
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Day-case pathways where appropriate</li>
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Enhanced recovery protocols</li>
                      </ul>
                    </div>
                    <div className="consultation-cta">
                      <h4 className="font-bold text-lg mb-4">Considering keyhole surgery?</h4>
                      <p className="mb-6">Book a consultation to see if this is appropriate for you.</p>
                      <div className="flex gap-4">
                        <a href="/contact" className="btn btn-primary">
                          <Calendar className="mr-2" size={16} />
                          Book Consultation
                        </a>
                        <a href="tel:02089499020" className="btn btn-secondary">
                          <Phone className="mr-2" size={16} />
                          Call Now
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Abdominal pain & GI symptoms */}
              <div id="abdominal-gi" className="service-detail-card" data-testid="abdominal-gi-service">
                <div className="service-detail-header">
                  <div className="service-detail-image"
                    style={{ backgroundImage: `url('https://images.unsplash.com/photo-1550831107-1553da8c8464?auto=format&fit=crop&w=1600&q=80')` }}>
                  </div>
                  <div className="service-detail-content">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="service-icon-large">
                        <Users className="text-white" size={40} />
                      </div>
                      <div>
                        <h2 className="heading-secondary text-white mb-2">Abdominal Pain & Gastrointestinal Symptoms</h2>
                        <p className="text-white opacity-90">Comprehensive assessment & management</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="service-detail-body">
                  <div className="grid grid-2 gap-12">
                    <div>
                      <h3 className="heading-tertiary mb-6">Common issues</h3>
                      <ul className="procedure-list">
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Bloating, indigestion, abdominal discomfort</li>
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Gallstone-related pain</li>
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Suspected hernia symptoms</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="heading-tertiary mb-6">Our approach</h3>
                      <p className="text-lg leading-relaxed">
                        Focused history, examination and targeted investigations to reach a clear diagnosis and plan.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* GORD */}
              <div id="gord" className="service-detail-card" data-testid="gord-service">
                <div className="service-detail-header">
                  <div className="service-detail-image"
                    style={{ backgroundImage: `url('https://images.unsplash.com/photo-1606431424031-dbdedefc5bb0?crop=entropy&cs=srgb&fm=jpg&q=85')` }}>
                  </div>
                  <div className="service-detail-content">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="service-icon-large">
                        <BookOpen className="text-white" size={40} />
                      </div>
                      <div>
                        <h2 className="heading-secondary text-white mb-2">Gastro-oesophageal Reflux Disease (GORD)</h2>
                        <p className="text-white opacity-90">Diagnosis and tailored management</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="service-detail-body">
                  <div className="grid grid-2 gap-12">
                    <div>
                      <h3 className="heading-tertiary mb-6">Symptoms addressed</h3>
                      <ul className="procedure-list">
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Heartburn and regurgitation</li>
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Chest discomfort linked to reflux</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="heading-tertiary mb-6">Management</h3>
                      <p className="text-lg leading-relaxed">Evidence-based medical and procedural options as appropriate.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gallbladder */}
              <div id="gallbladder" className="service-detail-card" data-testid="gallbladder-service">
                <div className="service-detail-header">
                  <div className="service-detail-image"
                    style={{ backgroundImage: `url('https://images.unsplash.com/photo-1606431424031-dbdedefc5bb0?crop=entropy&cs=srgb&fm=jpg&q=85')` }}>
                  </div>
                  <div className="service-detail-content">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="service-icon-large">
                        <Heart className="text-white" size={40} />
                      </div>
                      <div>
                        <h2 className="heading-secondary text-white mb-2">Gallstones & Gallbladder Surgery</h2>
                        <p className="text-white opacity-90">Laparoscopic cholecystectomy where indicated</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="service-detail-body">
                  <div className="grid grid-2 gap-12">
                    <div>
                      <h3 className="heading-tertiary mb-6">Conditions treated</h3>
                      <ul className="procedure-list">
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Gallstones (cholelithiasis)</li>
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Gallbladder inflammation</li>
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Bile duct stones</li>
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Gallbladder polyps</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="heading-tertiary mb-6">Techniques</h3>
                      <ul className="procedure-list">
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Laparoscopic cholecystectomy</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Hernia */}
              <div id="hernia" className="service-detail-card" data-testid="hernia-service">
                <div className="service-detail-header">
                  <div className="service-detail-image"
                    style={{ backgroundImage: `url('https://images.unsplash.com/photo-1606685622133-ed6fbaaf445c?crop=entropy&cs=srgb&fm=jpg&q=85')` }}>
                  </div>
                  <div className="service-detail-content">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="service-icon-large">
                        <Shield className="text-white" size={40} />
                      </div>
                      <div>
                        <h2 className="heading-secondary text-white mb-2">Hernia Assessment & Management</h2>
                        <p className="text-white opacity-90">Tailored repair options for different hernia types</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="service-detail-body">
                  <div className="grid grid-2 gap-12">
                    <div>
                      <h3 className="heading-tertiary mb-6">Hernia types</h3>
                      <ul className="procedure-list">
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Inguinal</li>
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Umbilical</li>
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Incisional</li>
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Complex abdominal</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="heading-tertiary mb-6">Repair methods</h3>
                      <ul className="procedure-list">
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Laparoscopic repair</li>
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Open tension-free repair</li>
                        {/* Robotic option removed as requested */}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Lipomas, lumps & bumps */}
              <div id="lipomas" className="service-detail-card" data-testid="lipomas-service">
                <div className="service-detail-header">
                  <div className="service-detail-image"
                    style={{ backgroundImage: `url('https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=1600&q=80')` }}>
                  </div>
                  <div className="service-detail-content">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="service-icon-large">
                        <Award className="text-white" size={40} />
                      </div>
                      <div>
                        <h2 className="heading-secondary text-white mb-2">Lipomas, Lumps & Bumps</h2>
                        <p className="text-white opacity-90">Assessment and management, including removal where appropriate</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="service-detail-body">
                  <div className="grid grid-2 gap-12">
                    <div>
                      <h3 className="heading-tertiary mb-6">Common presentations</h3>
                      <ul className="procedure-list">
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Lipomas (fatty lumps)</li>
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Sebaceous cysts</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="heading-tertiary mb-6">Management</h3>
                      <p className="text-lg leading-relaxed">Clinical review, imaging where needed, and day-case excision when indicated.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Non-surgical weight assessment & management */}
              <div id="weight-non-surgical" className="service-detail-card" data-testid="non-surgical-weight">
                <div className="service-detail-header">
                  <div className="service-detail-image"
                    style={{ backgroundImage: `url('https://images.unsplash.com/photo-1543363136-5ee0c7c6d16f?auto=format&fit=crop&w=1600&q=80')` }}>
                  </div>
                  <div className="service-detail-content">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="service-icon-large">
                        <Scale className="text-white" size={40} />
                      </div>
                      <div>
                        <h2 className="heading-secondary text-white mb-2">Non-Surgical Weight Assessment & Management</h2>
                        <p className="text-white opacity-90">Holistic review with structured packages</p>
                      </div>
                    </div>
                  </div>
                </div>

                

                <div className="service-detail-body">
                  <div className="grid grid-2 gap-12">
                    <div>
                      <h3 className="heading-tertiary mb-6">What’s included</h3>
                      <ul className="procedure-list">
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Comprehensive metabolic & lifestyle assessment</li>
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Nutrition & activity planning</li>
                        <li><CheckCircle size={16} className="text-green-500 mr-2" />Medication options where appropriate</li>
                      </ul>
                    </div>
                    <div className="consultation-cta">
                      <h4 className="font-bold text-lg mb-4">Start your tailored plan</h4>
                      <p className="mb-6">Discuss the most suitable non-surgical pathway for you.</p>
                      <div className="flex gap-4">
                        <a href="/contact" className="btn btn-primary">
                          <Calendar className="mr-2" size={16} />
                          Book Consultation
                        </a>
                        <a href="/patient-info" className="btn btn-secondary">
                          Learn More
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>


{/* Endoscopy */}
<div id="endoscopy" className="service-detail-card" data-testid="endoscopy-service">
  <div className="service-detail-header">
    <div
      className="service-detail-image"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1600&q=80')`,
      }}
    />
    <div className="service-detail-content">
      <div className="flex items-center gap-4 mb-6">
        <div className="service-icon-large">
          <BookOpen className="text-white" size={40} />
        </div>
        <div>
          <h2 className="heading-secondary text-white mb-2">Endoscopy</h2>
          <p className="text-white opacity-90">Diagnosis & treatment of upper and lower GI conditions</p>
        </div>
      </div>
    </div>
  </div>

  <div className="service-detail-body">
    <div className="grid grid-2 gap-12">
      <div>
        <h3 className="heading-tertiary mb-6">What we offer</h3>
        <ul className="procedure-list">
          <li><CheckCircle size={16} className="text-green-500 mr-2" />Gastroscopy (OGD)</li>
          {/* <li><CheckCircle size={16} className="text-green-500 mr-2" />Colonoscopy</li>
          <li><CheckCircle size={16} className="text-green-500 mr-2" />Polypectomy</li>
          <li><CheckCircle size={16} className="text-green-500 mr-2" />Oesophageal dilation (where indicated)</li> */}
          <li><CheckCircle size={16} className="text-green-500 mr-2" />Targeted biopsies & therapeutic procedures</li>
        </ul>
      </div>
      <div>
        <h3 className="heading-tertiary mb-6">What to expect</h3>
        <p className="text-lg leading-relaxed mb-6">
          Clear pre-procedure guidance, comfort-focused sedation options, and a concise post-procedure report with next steps.
        </p>
        <div className="consultation-cta">
          <h4 className="font-bold text-lg mb-4">Need an endoscopy?</h4>
          <p className="mb-6">Book an assessment to determine the most appropriate investigation.</p>
          <div className="flex gap-4">
            <a href="/contact" className="btn btn-primary">
              <Calendar className="mr-2" size={16} />
              Book Consultation
            </a>
            <a href="tel:02089499020" className="btn btn-secondary">
              <Phone className="mr-2" size={16} />
              Call Now
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

            </div>
          </div>
        </section>

        {/* Why Choose Us (text adjusted to remove robotic mention) */}
        <section className="section bg-gradient-modern" data-testid="why-choose-section">
          <div className="container">
            <div className="text-center mb-16">
              <h2 className="heading-secondary" data-testid="why-choose-title">
                Why Choose Professor Vasha Kaur?
              </h2>
              <p className="text-large" data-testid="why-choose-subtitle">
                Excellence in surgical care with a patient-first approach and proven outcomes
              </p>
            </div>

            <div className="grid grid-3 gap-8">
              <div className="card text-center" data-testid="expertise-card">
                <div className="card-header">
                  <div className="service-icon mx-auto mb-6">
                    <Scissors className="text-blue-600" size={40} />
                  </div>
                  <h3 className="heading-tertiary">Advanced Techniques</h3>
                </div>
                <div className="card-content">
                  <p className="mb-4">Utilising modern minimally invasive techniques for reduced pain and faster recovery.</p>
                  <div className="flex justify-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="text-yellow-400 fill-current" size={16} />
                    ))}
                  </div>
                </div>
              </div>

              <div className="card text-center" data-testid="personalized-card">
                <div className="card-header">
                  <div className="service-icon mx-auto mb-6">
                    <Heart className="text-blue-600" size={40} />
                  </div>
                  <h3 className="heading-tertiary">Personalised Care</h3>
                </div>
                <div className="card-content">
                  <p className="mb-4">Individual treatment plans tailored to each patient’s needs and goals.</p>
                  <div className="flex justify-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="text-yellow-400 fill-current" size={16} />
                    ))}
                  </div>
                </div>
              </div>

              <div className="card text-center" data-testid="excellence-card">
                <div className="card-header">
                  <div className="service-icon mx-auto mb-6">
                    <Award className="text-blue-600" size={40} />
                  </div>
                  <h3 className="heading-tertiary">Proven Excellence</h3>
                </div>
                <div className="card-content">
                  <p className="mb-4">Outstanding outcomes, safety, and patient satisfaction.</p>
                  <div className="flex justify-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="text-yellow-400 fill-current" size={16} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="section bg-medical-light parallax-section" data-testid="services-cta">
          <div className="container text-center">
            <div className="max-w-4xl mx-auto">
              <h2 className="heading-secondary mb-6">Ready to Schedule Your Consultation?</h2>
              <p className="text-large mb-8">
                Take the first step towards better health with expert care from {'Professor\u00A0Vasha\u00A0Kaur'} and her experienced medical team.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/contact" className="btn btn-primary btn-large">
                  <Clock className="mr-2" size={20} />
                  Book Consultation
                </a>
                <a href="/patient-info" className="btn btn-secondary btn-large">
                  Patient Information
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Services;
